import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import InvestorLayout from '../../components/InvestorLayout';
import { Download, FileText } from 'lucide-react';

export default function StatementDownload({ user, onLogout }) {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatement();
  }, []);

  const fetchStatement = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/investor/statement`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatement(response.data);
    } catch (error) {
      toast.error('Failed to fetch statement');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadJSON = () => {
    const dataStr = JSON.stringify(statement, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `investment-statement-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Statement downloaded successfully');
  };

  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/investor/statement/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `investment-statement-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('PDF statement downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF statement');
    }
  };

  const handleDownloadCSV = () => {
    if (!statement) return;
    
    // Create CSV for investments
    let csv = 'Investment Statement\n\n';
    csv += 'Investments\n';
    csv += 'Investment ID,Principal Amount,Interest Rate,Interest Type,Current Value,Status,Start Date,Maturity Date\n';
    
    statement.investments.forEach(inv => {
      csv += `${inv.investment_id},${inv.principal_amount},${inv.interest_rate}%,${inv.interest_type},${inv.current_value},${inv.status},${new Date(inv.start_date).toLocaleDateString()},${new Date(inv.maturity_date).toLocaleDateString()}\n`;
    });
    
    csv += '\n\nPayouts\n';
    csv += 'Payout ID,Investment ID,Amount,Type,Date,Status\n';
    
    statement.payouts.forEach(payout => {
      csv += `${payout.payout_id},${payout.investment_id},${payout.amount},${payout.payout_type},${new Date(payout.payout_date).toLocaleDateString()},${payout.status}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `investment-statement-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV statement downloaded successfully');
  };

  if (loading) {
    return (
      <InvestorLayout user={user} onLogout={onLogout} currentPage="statement">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading statement...</div>
        </div>
      </InvestorLayout>
    );
  }

  const totalInvested = statement?.investments?.reduce((sum, inv) => sum + inv.principal_amount, 0) || 0;
  const totalCurrentValue = statement?.investments?.reduce((sum, inv) => sum + inv.current_value, 0) || 0;
  const totalPayouts = statement?.payouts?.reduce((sum, p) => sum + p.amount, 0) || 0;

  return (
    <InvestorLayout user={user} onLogout={onLogout} currentPage="statement">
      <div className="space-y-6" data-testid="statement-download-page">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Investment Statement
          </h1>
          <p className="text-muted-foreground text-lg">Download your complete investment records</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Total Invested</p>
            <p className="text-3xl font-bold font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              ₹{totalInvested.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="card p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Current Value</p>
            <p className="text-3xl font-bold font-mono text-success" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              ₹{totalCurrentValue.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="card p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">Total Payouts</p>
            <p className="text-3xl font-bold font-mono text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              ₹{totalPayouts.toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        <div className="card p-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-accent/10 p-4 rounded-lg">
              <FileText className="w-8 h-8 text-accent" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Download Options
              </h2>
              <p className="text-muted-foreground">Export your investment statement in different formats</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              data-testid="download-pdf-button"
              onClick={handleDownloadPDF}
              className="flex items-center justify-center space-x-3 p-6 border-2 border-border rounded-lg hover:border-accent hover:bg-accent/5 transition-all"
            >
              <Download className="w-6 h-6 text-accent" />
              <div className="text-left">
                <p className="font-semibold">Download PDF</p>
                <p className="text-sm text-muted-foreground">Professional statement report</p>
              </div>
            </button>

            <button
              data-testid="download-json-button"
              onClick={handleDownloadJSON}
              className="flex items-center justify-center space-x-3 p-6 border-2 border-border rounded-lg hover:border-accent hover:bg-accent/5 transition-all"
            >
              <Download className="w-6 h-6 text-accent" />
              <div className="text-left">
                <p className="font-semibold">Download JSON</p>
                <p className="text-sm text-muted-foreground">Complete data in JSON format</p>
              </div>
            </button>

            <button
              data-testid="download-csv-button"
              onClick={handleDownloadCSV}
              className="flex items-center justify-center space-x-3 p-6 border-2 border-border rounded-lg hover:border-accent hover:bg-accent/5 transition-all"
            >
              <Download className="w-6 h-6 text-accent" />
              <div className="text-left">
                <p className="font-semibold">Download CSV</p>
                <p className="text-sm text-muted-foreground">Spreadsheet format for Excel</p>
              </div>
            </button>
          </div>
        </div>

        <div className="card">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Statement Summary
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-muted-foreground">Investor Name</span>
              <span className="font-semibold">{statement?.investor?.full_name}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-muted-foreground">Investor ID</span>
              <span className="font-mono">{statement?.investor?.investor_id}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-muted-foreground">Total Investments</span>
              <span className="font-semibold">{statement?.investments?.length || 0}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-muted-foreground">Total Payouts</span>
              <span className="font-semibold">{statement?.payouts?.length || 0}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-muted-foreground">Statement Generated</span>
              <span className="text-sm">
                {statement?.generated_at ? new Date(statement.generated_at).toLocaleString('en-IN') : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </InvestorLayout>
  );
}